import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router } from '@angular/router';
import { ProjectService } from '../../features/projects/services/projects.service';

@Component({
  selector: 'app-project-new',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    FormsModule,
    ReactiveFormsModule,
    NgxMatSelectSearchModule,
  ],
  templateUrl: './project-new.component.html',
  styleUrls: ['./project-new.component.scss'],
})
export class ProjectNewComponent {
  projectForm: FormGroup;
  loading: boolean = false;
  projectId: any | null = null;

  constructor(
    private toastr: ToastrService,
    private router: Router,
    private route: ActivatedRoute,
    private projectService: ProjectService
  ) {
    this.projectForm = this.createProjectForm();
  }

  ngOnInit() {
    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      this.projectId = id;
      this.fetchProjectById(Number(this.projectId));
    });
  }

  /** Create project form with all validators */
  private createProjectForm(): FormGroup {
    return new FormGroup({
      quoteCode: new FormControl('', [Validators.required]),
      orderCode: new FormControl('', [Validators.required]),
      firstName: new FormControl('', [Validators.required]),
      lastName: new FormControl('', [Validators.required]),
      projectCode: new FormControl('', [Validators.required]),
      projectName: new FormControl('', [Validators.required]),
      projectCost: new FormControl('', [Validators.required]),
      startDate: new FormControl('', [Validators.required]),
      endDate: new FormControl('', [Validators.required]),
    });
  }

  private fetchProjectById(id: number) {
    this.projectService.getProjectById(id).subscribe({
      next: (details) => {
        this.projectForm.patchValue({
          projectName: details.projectName,
          projectCode: details.projectCode,
          quoteCode: details.quoteData?.quoteCode || '',
          orderCode: details.orderData?.orderCode || '',
          firstName: details.customerData?.firstName || '',
          lastName: details.customerData?.lastName || '',
          projectCost: details.projectCost ?? '',
          startDate: details.startDate ? new Date(details.startDate) : '',
          endDate: details.endDate ? new Date(details.endDate) : '',
        });

        const fieldsToDisable = [
          'projectName',
          'projectCode',
          'quoteCode',
          'orderCode',
          'firstName',
          'lastName',
          'projectCost',
        ];

        fieldsToDisable.forEach((field) =>
          this.projectForm.get(field)?.disable()
        );
      },
      error: (error) => console.error('Error fetching project details:', error),
    });
  }

  endDateFilter = (d: Date | null): boolean => {
    const startDate = this.projectForm.get('startDate')?.value;
    if (!startDate) return true;
    return d ? d >= startDate : false;
  };

  startDateFilter = (d: Date | null): boolean => {
    const endDate = this.projectForm.get('endDate')?.value;
    if (!endDate) return true;
    return d ? d <= endDate : false;
  };

  onSubmit() {
    const values = this.projectForm?.value;
    if (!values?.startDate || !values?.endDate) {
      this.toastr.warning('Please fill mandatory fields!', 'Warning');
      return;
    }
    let createProjectModuleInput: any = {
      id: Number(this.projectId),
      startDate: values?.startDate ?? '',
      endDate: values?.endDate ?? '',
    };

    if (this.projectId) {
      this.loading = true;
      this.projectService
        .updateProjectWithId(createProjectModuleInput, Number(this.projectId))
        .subscribe({
          next: (response) => {
            this.toastr.success('Project Update successfully');
            this.router.navigate(['/projects']);
            this.loading = false;
          },
          error: (error) => {
            this.toastr.error(error.message || 'Project creation failed.');
            this.loading = false;
          },
        });
    }
  }

  onClearForm() {
    // this.projectForm.reset();
    this.router.navigate(['/projects']);
  }
}
